import { cloneElement, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { XIcon } from "lucide-react";

import { hasPermission } from "simplydo/core/permissions";
import { initUseQuery } from "simplydo/core/query";
import { useCurrentUser } from "simplydo/react/hooks/useCurrentUser";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  LabelDetail,
  Label as SLabel,
  type LabelSize,
} from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";

type LabelStackViewProps = {
  labels: string[];
  horizontal?: boolean;
  size?: LabelSize;
};

export const LabelStackView = ({
  labels,
  horizontal,
  size = "small",
}: LabelStackViewProps) => {
  if (!labels || labels.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: horizontal ? "nowrap" : "wrap",
        flexDirection: horizontal ? "row" : "column",
        gap: horizontal ? 2 : 4,
        overflow: horizontal ? "hidden" : undefined,
      }}
    >
      {labels?.map((label) => (
        <div key={label}>
          <LabelItem label={label} size={size} />
        </div>
      ))}
    </div>
  );
};

type LabelItemProps = {
  label: string;
  onRemove?: (label: string) => void;
  size?: LabelSize;
  style?: React.CSSProperties;
};

const StyledLabelContent = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

export const LabelItem = ({
  label,
  onRemove,
  size = "small",
  style,
}: LabelItemProps) => (
  // Default semantic padding makes the label too big for our use case
  // Default semantic color sets the label to .6 alpha which works for text but makes emojis look faded
  <SLabel
    size={size}
    style={{ padding: "0.2em 0.6em", color: "#5d5d5d", ...style }}
  >
    <StyledLabelContent>{label}</StyledLabelContent>
    {onRemove && (
      <LabelDetail>
        <Icon icon={XIcon} onClick={() => onRemove(label)} />
      </LabelDetail>
    )}
  </SLabel>
);

type LabelDropdownProps = {
  value: string[];
  onChange: (labels: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

// Searchable multi-select over the organisation's existing labels, new labels can be
// added by typing them in full
export const LabelDropdown = ({
  value,
  onChange,
  placeholder,
  disabled,
}: LabelDropdownProps) => {
  const { t } = useTranslation();
  const labelQuery = useQuery(initUseQuery(api.challenges.getLabels, []));

  const options = Array.from(
    new Set([...(labelQuery.data?.labels ?? []), ...value]),
  ).map((label) => ({ key: label, text: label, value: label }));

  return (
    <Dropdown
      fluid
      variant="outline"
      multiple="combo"
      search
      allowAdditions
      disabled={disabled}
      loading={labelQuery.isLoading}
      placeholder={placeholder ?? t("labelChooser.placeholder")}
      value={value}
      options={options}
      onChange={(_e, { value: selected }) => {
        const labels = ((selected as string[]) ?? [])
          .map((label) => label.trim())
          .filter(Boolean);
        const added = labels.filter(
          (label) => !(labelQuery.data?.labels ?? []).includes(label),
        );
        if (added.length) {
          // Make newly added labels searchable straight away
          api.setQueryData(
            api.challenges.getLabels,
            [],
            (prev) => ({
              ...prev,
              labels: [...(prev?.labels ?? []), ...added],
            }),
            { allowUndefined: true },
          );
        }
        onChange(Array.from(new Set(labels)));
      }}
    />
  );
};

type LabelChooserProps = {
  trigger?: React.ReactElement;
  tooltip?: string;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  challengeId?: string;
  existing?: string[];
  onComplete: (label: string) => void;
  onRemoveExisting: (label: string) => void;
};

export const LabelChooser = ({
  isOpen,
  onOpen,
  onClose,
  trigger,
  tooltip,
  disabled,
  challengeId,
  existing,
  onComplete,
  onRemoveExisting,
}: LabelChooserProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const user = useCurrentUser(api);

  const canManageChallenges = hasPermission(
    user,
    "org.manageChallenges",
    user?.organisation,
  );
  const canViewSettings = challengeId
    ? hasPermission(user, "challenge.editIdeaLabels", challengeId)
    : true;

  // Button handlers for manually opening and closing the chooser popup
  const closePopup = useCallback(() => {
    if (open && onClose) {
      onClose();
    }
    setOpen(false);
  }, [open, onClose]);

  const openPopup = useCallback(() => {
    if (open) {
      return closePopup();
    }
    if (!open && onOpen) onOpen();
    setOpen(true);
  }, [open, onOpen, closePopup]);

  const triggerElement = useMemo(() => {
    if (trigger) {
      return cloneElement(trigger, {
        onClick: disabled ? () => {} : openPopup,
      } as React.HTMLAttributes<HTMLElement>);
    }
    return null;
  }, [trigger, openPopup, disabled]);

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen);
    }
  }, [open, isOpen]);

  const updateLabels = (labels: string[]) => {
    labels
      .filter((label) => !existing?.includes(label))
      .forEach((label) => onComplete(label));
    (existing ?? [])
      .filter((label) => !labels.includes(label))
      .forEach((label) => onRemoveExisting(label));
  };

  return (
    <>
      <Modal open={!!open} onClose={() => closePopup()}>
        <ModalHeader>{t("labelChooser.title")}</ModalHeader>
        <ModalContent>
          <p>{t("labelChooser.info")}</p>
          <H4>
            {t("labelChooser.labels")}
            <Subheader>{t("labelChooser.searchInfo")}</Subheader>
          </H4>
          <LabelDropdown
            value={existing ?? []}
            onChange={updateLabels}
            disabled={!(canManageChallenges || canViewSettings)}
          />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => closePopup()}>{t("generic.close")}</Button>
        </ModalActions>
      </Modal>
      <Popup
        on="hover"
        trigger={triggerElement}
        disabled={!tooltip || disabled}
      >
        {tooltip}
      </Popup>
    </>
  );
};
